import { createContext, use, useCallback, useEffect, type ReactNode } from 'react';
import { STORAGE_KEYS } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { themeChanged } from '@/store/slices';
import type { UiState } from '@/types';

type Theme = UiState['theme'];

interface ThemeContextValue {
  theme: Theme;
  /** What is actually on screen once 'system' has been resolved. */
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Applies the theme as a class on <html>, which is what shadcn's components
 * respond to.
 *
 * The equivalent script in index.html runs before React mounts so there is no
 * flash on load. This handles changes after that, and keeps 'system' live: if
 * the OS flips to dark while the app is open, the page follows.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.ui.theme);

  const apply = useCallback((next: Theme) => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const dark = next === 'dark' || (next === 'system' && prefersDark);
    document.documentElement.classList.toggle('dark', dark);
  }, []);

  useEffect(() => {
    apply(theme);

    if (theme !== 'system') return;

    // Only while following the system does an OS change matter.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      apply('system');
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, [theme, apply]);

  const setTheme = useCallback(
    (next: Theme) => {
      dispatch(themeChanged(next));
      try {
        if (next === 'system') localStorage.removeItem(STORAGE_KEYS.theme);
        else localStorage.setItem(STORAGE_KEYS.theme, next);
      } catch {
        // Preference will not survive a reload; the app still works.
      }
    },
    [dispatch],
  );

  const prefersDark =
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const resolved: 'light' | 'dark' = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;

  return <ThemeContext value={{ theme, resolved, setTheme }}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const context = use(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
